import {
  DateSearchSelect,
  InputSearch,
  MultipleSearchSelect
} from '@/components/custom/erp/data-table-v2/search-inputs';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField, SearchSearchColumnDef } from '@/components/custom/erp';
import { accountSearchColumns, customerSearchColumns, hoaDonSearchColumns, QUERY_KEYS } from '@/constants';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import type { InputTableCellParams, InputTableColumn } from '@/components/custom/erp';
import { AccountModel, KhachHang, PhieuThu } from '@/types/schemas';
import type { PhieuThuInvoiceLookup } from '@/types/schemas';
import { formatDate, formatMoney } from '@/lib/formatUtils';
import type { ReceiptCellValue } from './types';
import { FormMode } from '@/types/form';

const STATUS_OPTIONS = [
  { value: '0', label: 'Chưa ghi sổ' },
  { value: '3', label: 'Chờ duyệt' },
  { value: '5', label: 'Đã ghi sổ' }
];

const MA_NGV_OPTIONS = [
  { value: '1', label: '1. Thu theo hóa đơn' },
  { value: '2', label: '2. Thu theo đối tượng' },
  { value: '3', label: '3. Thu khác' }
];

export const getReceiptVoucherColumns = (
  handleOpenViewDialog: (row: PhieuThu) => void,
  totalAmount?: number | null
): SearchSearchColumnDef[] => [
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 120,
    headerAlign: 'center',
    renderCell: params => {
      const status = params.row.status;
      const option = STATUS_OPTIONS.find(opt => opt.value === status);
      return option?.label || '';
    },
    renderSearchInput: params => <MultipleSearchSelect {...params} options={STATUS_OPTIONS} />
  },
  {
    field: 'so_ct',
    headerName: 'Số chứng từ',
    width: 150,
    headerAlign: 'center',
    renderCell: params => (
      <button
        className='cursor-pointer hover:text-blue-500 hover:underline'
        onClick={e => {
          e.stopPropagation();
          handleOpenViewDialog(params.row);
        }}
      >
        {params.row.so_ct}
      </button>
    )
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày chứng từ',
    width: 150,
    renderCell: params => formatDate(params.row.ngay_ct),
    renderSearchInput: params => <DateSearchSelect {...params} />
  },
  {
    field: 'ma_kh',
    headerName: 'Mã đối tượng',
    width: 150,
    headerAlign: 'center',
    renderCell: params => params.row.ma_kh_data?.customer_code || params.row.ma_kh
  },
  {
    field: 'ten_kh',
    headerName: 'Tên đối tượng',
    width: 200,
    headerAlign: 'center',
    renderCell: params => params.row.ma_kh_data?.customer_name || params.row.ten_kh
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 250,
    headerAlign: 'center'
  },
  {
    field: 'tk',
    headerName: 'Tài khoản nợ',
    width: 120,
    headerAlign: 'center',
    renderCell: params => params.row.tk_data?.code || params.row.tk
  },
  {
    field: 't_tien',
    headerName: 'Tổng tiền',
    width: 150,
    type: 'number',
    align: 'right',
    renderCell: (params: DataTableRenderCellParams) => formatMoney(Number(params.value || 0)),
    renderSearchInput: params => (
      <InputSearch
        {...params}
        onKeyDown={params.onKeyDown ?? (() => {})}
        suffixContent={
          <div className='flex items-center gap-2'>
            <span className='text-lg font-bold'>&#931;</span>
            <span className='font-semibold text-main'>
              {typeof totalAmount === 'number' ? formatMoney(totalAmount) : '—'}
            </span>
          </div>
        }
      />
    )
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 100,
    align: 'center',
    renderCell: params => params.row.ma_nt_data?.ma_nt || params.row.ma_nt
  },
  {
    field: 'ma_ngv',
    headerName: 'Loại phiếu thu',
    width: 150,
    headerAlign: 'center',
    renderCell: params => {
      const status = params.row.ma_ngv;
      const option = MA_NGV_OPTIONS.find(opt => opt.value === status);
      return option?.label || '';
    },
    renderSearchInput: params => <MultipleSearchSelect {...params} options={MA_NGV_OPTIONS} />
  },
  {
    field: 'username2',
    headerName: 'Người sửa',
    width: 150
  },
  {
    field: 'datetime2',
    headerName: 'Ngày sửa',
    width: 150,
    renderSearchInput: params => <DateSearchSelect {...params} />
  }
];

export const getReceiptVoucherDetailColumns = (
  onCellValueChange: (rowUuid: string, field: string, newValue: ReceiptCellValue) => void,
  ma_ngv?: string,
  dien_giai?: string,
  formMode?: FormMode
): InputTableColumn[] => {
  switch (ma_ngv) {
    case '1': // Thu theo hóa đơn - Full columns
      return [
        {
          field: 'dien_giai',
          headerName: 'Diễn giải',
          width: 200,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='dien_giai'
              type='text'
              value={params.row.dien_giai || dien_giai || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'dien_giai', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'ma_kh',
          headerName: 'Mã đối tượng',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<KhachHang>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
              searchColumns={customerSearchColumns}
              columnDisplay='customer_code'
              dialogTitle='Danh mục khách hàng'
              value={params.row.ma_kh_data?.customer_code || ''}
              disabled={formMode === 'view'}
              onRowSelection={(row: KhachHang) => {
                onCellValueChange(params.row.uuid, 'ma_kh_data', row);
              }}
            />
          ),
          pasteConfig: {
            lookupKey: 'ma_kh',
            resultField: 'ma_kh_data',
            detailEndpoint: QUERY_KEYS.KHACH_HANG
          }
        },
        {
          field: 'ten_kh',
          headerName: 'Tên đối tượng',
          width: 200,
          renderCell: (params: InputTableCellParams) => params.row.ma_kh_data?.customer_name,
          nonWritable: true
        },
        {
          field: 'du_cn',
          headerName: 'Dư công nợ',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='du_cn' type='text' value={params.row.ma_kh_data?.du_cn || ''} />
          ),
          nonWritable: true
        },
        {
          field: 'id_hd',
          headerName: 'Hóa đơn',
          width: 190,
          renderCell: (params: InputTableCellParams) => (
            <SearchField
              type='text'
              searchEndpoint={`/${QUERY_KEYS.HOA_DON_THEO_KHACH_HANG}/?ma_kh=${params.row.ma_kh_data?.uuid}&document_type=PHIEU_THU`}
              searchColumns={hoaDonSearchColumns}
              columnDisplay='so_ct'
              dialogTitle='Hoá đơn'
              disabled={!params.row.ma_kh_data?.uuid || formMode === 'view'}
              value={params.row.id_hd_data?.so_ct || ''}
              onRowSelection={(row: PhieuThuInvoiceLookup) => onCellValueChange(params.row.uuid, 'id_hd_data', row)}
            />
          )
        },
        {
          field: 'so_ct0_hd',
          headerName: 'Số hóa đơn',
          width: 170,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='so_ct0_hd' type='text' value={params.row.id_hd_data?.so_ct} />
          ),
          nonWritable: true
        },
        {
          field: 'ngay_ct_hd',
          headerName: 'Ngày hóa đơn',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='ngay_ct_hd' type='date' value={params.row.id_hd_data?.ngay_ct} />
          ),
          nonWritable: true
        },
        {
          field: 'tk_co',
          headerName: 'Tài khoản có',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='tk_co'
              type='text'
              value={params.row.id_hd_data?.tk_data?.tk || params.row.tk_co_data?.code}
            />
          ),
          nonWritable: true
        },
        {
          field: 'ma_nt_hd',
          headerName: 'Ngoại tệ',
          width: 100,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='ma_nt' type='text' value={params.row.id_hd_data?.ngoai_te} />
          ),
          nonWritable: true
        },
        {
          field: 'ty_gia_hd',
          headerName: 'Tỷ giá hđ',
          width: 100,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='ty_gia_hd'
              type='number'
              value={params.row.id_hd_data?.ty_gia_hd || params.row.ty_gia_hd || 1}
            />
          ),
          nonWritable: true
        },
        {
          field: 'tien_hd_nt',
          headerName: 'Tiền trên hđ',
          width: 180,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='tien_hd_nt' type='number' value={params.row.id_hd_data?.tien_hd_nt} />
          ),
          nonWritable: true
        },
        {
          field: 'da_pb_nt',
          headerName: 'Đã phân bổ',
          width: 180,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='da_pb_nt'
              type='number'
              value={params.row.id_hd_data?.da_thanh_toan ?? params.row.id_hd_data?.da_pb_nt ?? 0}
            />
          ),
          nonWritable: true
        },
        {
          field: 'cl_nt',
          headerName: 'Còn lại',
          width: 180,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='cl_nt' type='number' value={params.row.id_hd_data?.tien_con_phai_tt} />
          ),
          nonWritable: true
        },
        {
          field: 'tien_nt',
          headerName: 'Tiền VND',
          width: 180,
          type: 'number',
          align: 'right',
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='tien_nt'
              type='number'
              value={params.row.tien_nt}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
              disabled={formMode === 'view'}
            />
          )
        }
      ];

    case '2': // Thu theo đối tượng - Simplified columns
    case '3': // Thu khác - Simplified columns
    default:
      return [
        {
          field: 'dien_giai',
          headerName: 'Diễn giải',
          width: 200,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='dien_giai'
              type='text'
              value={params.row.dien_giai || dien_giai || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'dien_giai', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'ma_kh',
          headerName: 'Mã đối tượng',
          width: 170,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<KhachHang>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
              searchColumns={customerSearchColumns}
              columnDisplay='customer_code'
              dialogTitle='Danh mục khách hàng'
              value={params.row.ma_kh_data?.customer_code || ''}
              disabled={formMode === 'view'}
              onRowSelection={(row: KhachHang) => {
                onCellValueChange(params.row.uuid, 'ma_kh_data', row);
              }}
            />
          ),
          pasteConfig: {
            lookupKey: 'ma_kh',
            resultField: 'ma_kh_data',
            detailEndpoint: QUERY_KEYS.KHACH_HANG
          }
        },
        {
          field: 'ten_kh',
          headerName: 'Tên đối tượng',
          width: 230,
          renderCell: (params: InputTableCellParams) => (
            <div className='px-2 py-1'>{params.row.ma_kh_data?.customer_name || ''}</div>
          ),
          nonWritable: true
        },
        {
          field: 'du_cong_no',
          headerName: 'Dư công nợ',
          width: 120,
          nonWritable: true
        },
        {
          field: 'so_hd',
          headerName: 'Số hóa đơn',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='so_hd'
              type='text'
              value={params.row.id_hd_data?.so_ct || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_hd', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'tk_co',
          headerName: 'Tài khoản có',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<AccountModel>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              searchColumns={accountSearchColumns}
              columnDisplay='code'
              dialogTitle='Danh mục tài khoản'
              value={params.row.tk_co_data?.code || ''}
              disabled={formMode === 'view'}
              onRowSelection={(row: AccountModel) => {
                onCellValueChange(params.row.uuid, 'tk_co_data', row);
              }}
            />
          ),
          nonWritable: true
        },
        {
          field: 'tien_nt',
          headerName: 'Tiền VND',
          width: 150,
          align: 'right',
          type: 'number',
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='tien_nt'
              type='number'
              placeholder='0'
              value={params.row.tien_nt}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
              disabled={formMode === 'view'}
            />
          )
        }
      ];
  }
};

export const getInputTableColumns = (): InputTableColumn[] => [
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 300,
    renderCell: params => params.row.dien_giai
  },
  {
    field: 'ma_kh',
    headerName: 'Mã đối tượng',
    width: 120,
    renderCell: params => params.row.ma_kh_data?.customer_code
  },
  {
    field: 'ten_kh',
    headerName: 'Tên đối tượng',
    width: 220,
    renderCell: params => params.row.ma_kh_data?.customer_name
  },
  {
    field: 'du_cn',
    headerName: 'Dư công nợ',
    width: 120,
    renderCell: params => params.row.du_cn
  },
  {
    field: 'id_hd',
    headerName: 'Hóa đơn',
    width: 180,
    renderCell: params => params.row.id_hd_data?.so_ct
  },
  {
    field: 'ngay_hd',
    headerName: 'Ngày hóa đơn',
    width: 120,
    renderCell: params => formatDate(params.row.id_hd_data?.ngay_ct)
  },
  {
    field: 'tk_co',
    headerName: 'Tài khoản có',
    width: 120,
    renderCell: params => params.row.tk_co_data?.code
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 100,
    renderCell: params => params.row.id_hd_data?.ngoai_te
  },
  {
    field: 'ty_gia_hd',
    headerName: 'Tỷ giá hđ',
    width: 100,
    renderCell: params => params.row.id_hd_data?.ty_gia_hd
  },
  {
    field: 'da_pb_nt',
    headerName: 'Đã phân bổ',
    width: 120,
    renderCell: params => params.row.id_hd_data?.da_thanh_toan ?? params.row.id_hd_data?.da_pb_nt
  },
  {
    field: 'cl_nt',
    headerName: 'Còn lại',
    width: 120,
    renderCell: params => params.row.id_hd_data?.tien_con_phai_tt
  },
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    width: 150,
    renderCell: params => params.row.tien_nt
  }
];
